import Phaser from 'phaser';
import polygonClipping from 'polygon-clipping';

type Coord = [number, number];
type Ring = Coord[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

interface Territory {
  playerId: string;
  color: number;
  polygon: MultiPolygon; // polygon-clipping 형식의 폴리곤
}

/**
 * 하이브리드 영역 관리 시스템
 *
 * 1. 그리드 데이터: 빠른 소유권 조회용 (중첩 방지)
 * 2. 폴리곤 데이터: 부드러운 렌더링용 (polygon-clipping 사용)
 *
 * 영역 연산:
 * - Union: 내 영역 + 새로운 트레일 영역
 * - Difference: 적 영역 - 내가 차지한 영역
 */
export class TerritoryManager {
  private graphics: Phaser.GameObjects.Graphics;

  // 그리드 데이터: 빠른 조회용
  private grid: (string | null)[][] = [];
  private cellSize: number = 16; // 8 -> 16으로 증가 (성능 최적화)
  private gridWidth: number;
  private gridHeight: number;

  // 각 플레이어의 그리드 바운딩 박스 캐시
  private gridBounds: Map<string, { minX: number; maxX: number; minY: number; maxY: number }> = new Map();

  // 폴리곤 데이터: 렌더링용
  private territories: Map<string, Territory> = new Map();

  // 영역 면적 캐시 (폴리곤 변경 시 무효화) - 정점 수에 비례하는 면적 계산이
  // HUD/리더보드에서 반복 호출되므로 캐시 필수
  private areaCache: Map<string, number> = new Map();

  // 월드 정보
  private worldCenterX: number;
  private worldCenterY: number;
  private worldRadius: number;

  // 캐릭터 위치
  private characterPositions: Map<string, { x: number; y: number }> = new Map();

  // 트레일 시작점 (꼬리의 뿌리) - 트레일 중인 캐릭터만
  private trailRoots: Map<string, { x: number; y: number }> = new Map();

  // 꼬리 단절 시 사망 콜백
  private onTrailCutCallback?: (playerId: string) => void;

  constructor(
    scene: Phaser.Scene,
    centerX: number,
    centerY: number,
    radius: number
  ) {
    this.worldCenterX = centerX;
    this.worldCenterY = centerY;
    this.worldRadius = radius;

    // 그리드 초기화
    const worldSize = radius * 2 + 200;
    this.gridWidth = Math.ceil(worldSize / this.cellSize);
    this.gridHeight = Math.ceil(worldSize / this.cellSize);

    this.grid = [];
    for (let y = 0; y < this.gridHeight; y++) {
      this.grid[y] = [];
      for (let x = 0; x < this.gridWidth; x++) {
        this.grid[y][x] = null;
      }
    }

    this.graphics = scene.add.graphics();
    this.graphics.setDepth(2);
  }

  // === 좌표 변환 ===

  private worldToGrid(wx: number, wy: number): { gx: number; gy: number } {
    const offsetX = this.worldCenterX - this.worldRadius - 100;
    const offsetY = this.worldCenterY - this.worldRadius - 100;
    return {
      gx: Math.floor((wx - offsetX) / this.cellSize),
      gy: Math.floor((wy - offsetY) / this.cellSize),
    };
  }

  private isValidGrid(gx: number, gy: number): boolean {
    return gx >= 0 && gx < this.gridWidth && gy >= 0 && gy < this.gridHeight;
  }

  // === 폴리곤 변환 ===

  private pointsToClipper(points: { x: number; y: number }[]): Polygon {
    if (points.length < 3) return [];
    const ring: Ring = points.map(p => [p.x, p.y]);
    // 닫힌 폴리곤 확인
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
    return [ring];
  }

  private clipperToPoints(multiPoly: MultiPolygon): { x: number; y: number }[][] {
    return multiPoly.map(polygon => {
      const ring = polygon[0]; // 외곽 링만 사용
      if (!ring || ring.length < 3) return [];
      return ring.map(coord => ({ x: coord[0], y: coord[1] }));
    }).filter(p => p.length >= 3);
  }

  // === 그리드 업데이트 ===

  private updateGridFromPolygon(playerId: string, multiPoly: MultiPolygon): void {
    // 폴리곤이 바뀌었으므로 면적 캐시 무효화 (모든 폴리곤 변경 경로가 이 함수를 거친다)
    this.areaCache.delete(playerId);

    const offsetX = this.worldCenterX - this.worldRadius - 100;
    const offsetY = this.worldCenterY - this.worldRadius - 100;

    // 캐시된 바운딩 박스 내에서만 기존 셀 제거 (최적화)
    const oldBounds = this.gridBounds.get(playerId);
    if (oldBounds) {
      for (let gy = oldBounds.minY; gy <= oldBounds.maxY; gy++) {
        for (let gx = oldBounds.minX; gx <= oldBounds.maxX; gx++) {
          if (this.grid[gy]?.[gx] === playerId) {
            this.grid[gy][gx] = null;
          }
        }
      }
    }

    // 새 바운딩 박스 계산
    let newMinGX = Infinity, newMaxGX = -Infinity;
    let newMinGY = Infinity, newMaxGY = -Infinity;

    // 새 폴리곤 내부 셀 설정
    for (const polygon of multiPoly) {
      const ring = polygon[0];
      if (!ring || ring.length < 3) continue;

      // 바운딩 박스
      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;
      for (const [x, y] of ring) {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }

      const gxMin = Math.max(0, Math.floor((minX - offsetX) / this.cellSize));
      const gxMax = Math.min(this.gridWidth - 1, Math.ceil((maxX - offsetX) / this.cellSize));
      const gyMin = Math.max(0, Math.floor((minY - offsetY) / this.cellSize));
      const gyMax = Math.min(this.gridHeight - 1, Math.ceil((maxY - offsetY) / this.cellSize));

      newMinGX = Math.min(newMinGX, gxMin);
      newMaxGX = Math.max(newMaxGX, gxMax);
      newMinGY = Math.min(newMinGY, gyMin);
      newMaxGY = Math.max(newMaxGY, gyMax);

      // 스캔라인 채우기: 행마다 링과의 교차 x좌표를 구해 짝 사이를 채운다.
      // (이전의 셀별 point-in-polygon은 셀 수 × 링 정점 수라서
      //  영역이 커지면 확장 한 번에 수천만 연산 = 화면 멈춤의 원인이었다)
      for (let gy = gyMin; gy <= gyMax; gy++) {
        const wy = offsetY + gy * this.cellSize + this.cellSize / 2;

        const intersections: number[] = [];
        for (let i = 0; i < ring.length; i++) {
          const [x1, y1] = ring[i];
          const [x2, y2] = ring[(i + 1) % ring.length];
          if ((y1 <= wy && y2 > wy) || (y2 <= wy && y1 > wy)) {
            intersections.push(x1 + ((wy - y1) / (y2 - y1)) * (x2 - x1));
          }
        }
        intersections.sort((a, b) => a - b);

        for (let k = 0; k + 1 < intersections.length; k += 2) {
          const gxStart = Math.max(gxMin, Math.ceil((intersections[k] - offsetX - this.cellSize / 2) / this.cellSize));
          const gxEnd = Math.min(gxMax, Math.floor((intersections[k + 1] - offsetX - this.cellSize / 2) / this.cellSize));
          for (let gx = gxStart; gx <= gxEnd; gx++) {
            this.grid[gy][gx] = playerId;
          }
        }
      }
    }

    // 새 바운딩 박스 캐시
    if (newMinGX !== Infinity) {
      this.gridBounds.set(playerId, {
        minX: newMinGX, maxX: newMaxGX,
        minY: newMinGY, maxY: newMaxGY
      });
    }
  }

  private pointInPolygon(x: number, y: number, ring: Ring): boolean {
    let inside = false;
    const n = ring.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }

  // === 공개 API ===

  updateCharacterPosition(playerId: string, x: number, y: number): void {
    this.characterPositions.set(playerId, { x, y });
  }

  /**
   * 트레일 시작 시 뿌리 위치 저장
   */
  setTrailRoot(playerId: string, x: number, y: number): void {
    this.trailRoots.set(playerId, { x, y });
  }

  /**
   * 트레일 종료 시 뿌리 제거
   */
  clearTrailRoot(playerId: string): void {
    this.trailRoots.delete(playerId);
  }

  /**
   * 꼬리 단절 콜백 설정
   */
  setOnTrailCutCallback(callback: (playerId: string) => void): void {
    this.onTrailCutCallback = callback;
  }

  /**
   * 꼬리 단절 체크 - 트레일 뿌리가 자신의 영역 밖이면 사망
   * @param excludePlayerId 이 플레이어는 체크에서 제외 (영역 확장 완료 중인 플레이어)
   */
  private checkTrailCuts(excludePlayerId?: string): void {
    if (!this.onTrailCutCallback) return;

    for (const [playerId, root] of this.trailRoots) {
      // 영역 확장 중인 플레이어는 제외 (곧 trailRoot가 클리어됨)
      if (playerId === excludePlayerId) continue;

      // 꼬리 뿌리 주변을 여러 점 체크 (더 관대한 판정)
      if (!this.isTrailRootSafe(playerId, root.x, root.y)) {
        this.onTrailCutCallback(playerId);
      }
    }
  }

  /**
   * 꼬리 뿌리가 안전한지 체크 (주변 영역도 확인)
   */
  private isTrailRootSafe(playerId: string, x: number, y: number): boolean {
    // 중앙 체크
    if (this.isInsideOwnTerritory(playerId, x, y)) return true;

    // 주변 4방향 체크 (cellSize 거리)
    const offset = this.cellSize;
    const offsets = [
      { dx: -offset, dy: 0 },
      { dx: offset, dy: 0 },
      { dx: 0, dy: -offset },
      { dx: 0, dy: offset },
    ];

    for (const { dx, dy } of offsets) {
      if (this.isInsideOwnTerritory(playerId, x + dx, y + dy)) {
        return true;
      }
    }

    return false;
  }

  createInitialTerritory(playerId: string, x: number, y: number, color: number, radius: number = 80): void {
    this.characterPositions.set(playerId, { x, y });

    // 원형 폴리곤 생성
    const points: { x: number; y: number }[] = [];
    const segments = 32;
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push({
        x: x + Math.cos(angle) * radius,
        y: y + Math.sin(angle) * radius,
      });
    }

    const newPoly: MultiPolygon = [this.pointsToClipper(points)];

    // 다른 영역에서 빼기 (Difference)
    this.territories.forEach((territory, otherId) => {
      if (otherId === playerId) return;
      try {
        const result = polygonClipping.difference(territory.polygon, newPoly);
        if (result.length > 0) {
          // 분할 체크
          const charPos = this.characterPositions.get(otherId);
          if (charPos && result.length > 1) {
            // 캐릭터가 있는 폴리곤만 유지
            territory.polygon = this.keepPolygonWithPoint(result, charPos);
          } else {
            territory.polygon = result;
          }
          this.updateGridFromPolygon(otherId, territory.polygon);
        }
      } catch (e) {
        console.warn('Difference failed:', e);
      }
    });

    // 새 영역 저장
    this.territories.set(playerId, { playerId, color, polygon: newPoly });
    this.updateGridFromPolygon(playerId, newPoly);

    this.render();
  }

  /**
   * 플레이어 영역 제거 (접속 종료/사망 시)
   */
  removePlayerTerritory(playerId: string): void {
    // 영역 제거
    this.territories.delete(playerId);
    this.areaCache.delete(playerId);

    // 그리드에서 해당 플레이어 셀 제거
    const bounds = this.gridBounds.get(playerId);
    if (bounds) {
      for (let gy = bounds.minY; gy <= bounds.maxY; gy++) {
        for (let gx = bounds.minX; gx <= bounds.maxX; gx++) {
          if (this.isValidGrid(gx, gy) && this.grid[gy][gx] === playerId) {
            this.grid[gy][gx] = null;
          }
        }
      }
      this.gridBounds.delete(playerId);
    }

    // 캐릭터 위치 제거
    this.characterPositions.delete(playerId);

    // 트레일 루트 제거
    this.trailRoots.delete(playerId);

    this.render();
  }

  isInsideOwnTerritory(playerId: string, x: number, y: number): boolean {
    const { gx, gy } = this.worldToGrid(x, y);
    if (!this.isValidGrid(gx, gy)) return false;
    return this.grid[gy][gx] === playerId;
  }

  getOwnerAt(x: number, y: number): string | null {
    const { gx, gy } = this.worldToGrid(x, y);
    if (!this.isValidGrid(gx, gy)) return null;
    return this.grid[gy][gx];
  }

  /**
   * 특정 위치의 영역 색상 반환
   */
  getTerritoryColorAt(x: number, y: number): number | null {
    const ownerId = this.getOwnerAt(x, y);
    if (!ownerId) return null;
    const territory = this.territories.get(ownerId);
    return territory ? territory.color : null;
  }

  isPositionFree(x: number, y: number): boolean {
    return this.getOwnerAt(x, y) === null;
  }

  /**
   * 안전한 리스폰 위치 찾기
   * 초기 영역 반경(80) + 여유 공간(40)을 고려하여 주변 영역 전체를 체크
   */
  findSafeRespawnPosition(
    centerX: number,
    centerY: number,
    worldRadius: number,
    maxAttempts: number = 100
  ): { x: number; y: number } {
    const safeRadius = 120; // 초기 영역 80 + 여유 40

    for (let i = 0; i < maxAttempts; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 200 + Math.random() * (worldRadius - 400);
      const x = centerX + Math.cos(angle) * dist;
      const y = centerY + Math.sin(angle) * dist;

      // 중심점 + 8방향 체크로 영역 전체가 비어있는지 확인
      if (this.isAreaFree(x, y, safeRadius)) {
        return { x, y };
      }
    }

    // 실패 시 더 넓은 범위에서 재시도
    for (let i = 0; i < maxAttempts; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = worldRadius * 0.7 + Math.random() * (worldRadius * 0.25);
      const x = centerX + Math.cos(angle) * dist;
      const y = centerY + Math.sin(angle) * dist;

      if (this.isAreaFree(x, y, safeRadius)) {
        return { x, y };
      }
    }

    // 최후의 수단: 맵 가장자리
    const angle = Math.random() * Math.PI * 2;
    return {
      x: centerX + Math.cos(angle) * worldRadius * 0.9,
      y: centerY + Math.sin(angle) * worldRadius * 0.9,
    };
  }

  /**
   * 특정 위치 주변 영역이 비어있는지 확인
   */
  private isAreaFree(x: number, y: number, radius: number): boolean {
    // 중심점 체크
    if (!this.isPositionFree(x, y)) return false;

    // 8방향 + 4방향 추가 체크 (총 12개 점)
    const checkPoints = [
      { dx: radius, dy: 0 },
      { dx: -radius, dy: 0 },
      { dx: 0, dy: radius },
      { dx: 0, dy: -radius },
      { dx: radius * 0.7, dy: radius * 0.7 },
      { dx: radius * 0.7, dy: -radius * 0.7 },
      { dx: -radius * 0.7, dy: radius * 0.7 },
      { dx: -radius * 0.7, dy: -radius * 0.7 },
      { dx: radius * 0.5, dy: 0 },
      { dx: -radius * 0.5, dy: 0 },
      { dx: 0, dy: radius * 0.5 },
      { dx: 0, dy: -radius * 0.5 },
    ];

    for (const { dx, dy } of checkPoints) {
      if (!this.isPositionFree(x + dx, y + dy)) {
        return false;
      }
    }

    return true;
  }

  expandTerritory(
    playerId: string,
    trailPoints: { x: number; y: number }[]
  ): { x: number; y: number }[] | null {
    if (trailPoints.length < 3) return null;

    const territory = this.territories.get(playerId);
    if (!territory) return null;

    try {
      // 트레일 포인트 정리 (중복 제거, 너무 가까운 점 제거)
      const cleanedTrail = this.cleanupPoints(trailPoints);
      if (cleanedTrail.length < 3) return null;

      // 트레일 폴리곤 생성
      const trailPoly = this.createTrailPolygon(territory.polygon, cleanedTrail);
      if (!trailPoly || !this.isValidPolygon(trailPoly)) {
        console.warn('Invalid trail polygon');
        return null;
      }

      // Union: 기존 영역 + 트레일 영역
      let expanded: MultiPolygon;
      try {
        expanded = polygonClipping.union(territory.polygon, [trailPoly]);
      } catch (e) {
        console.warn('Union failed, trying alternative approach:', e);
        // 대안: 트레일만으로 새 영역 생성
        expanded = [trailPoly];
      }

      if (expanded.length === 0) return null;

      // 유효한 폴리곤만 유지
      expanded = this.filterValidPolygons(expanded);
      if (expanded.length === 0) return null;

      // 정점 누적 방지: union 결과를 단순화해서 저장
      expanded = this.simplifyMultiPolygon(expanded);
      if (expanded.length === 0) return null;

      territory.polygon = expanded;

      // 다른 영역에서 빼기 (Difference)
      this.territories.forEach((otherTerritory, otherId) => {
        if (otherId === playerId) return;
        try {
          const result = polygonClipping.difference(otherTerritory.polygon, expanded);
          const charPos = this.characterPositions.get(otherId);

          if (result.length === 0) {
            // 영역 완전 파괴 - 작은 원형 영역으로 리셋
            if (charPos) {
              this.resetTerritoryAroundPoint(otherId, charPos, otherTerritory.color);
            }
          } else if (charPos && result.length > 1) {
            // 분할됨 - 캐릭터가 있는 쪽만 유지
            otherTerritory.polygon = this.simplifyMultiPolygon(this.keepPolygonWithPoint(result, charPos));
            this.updateGridFromPolygon(otherId, otherTerritory.polygon);
          } else {
            otherTerritory.polygon = this.simplifyMultiPolygon(result);
            this.updateGridFromPolygon(otherId, otherTerritory.polygon);
          }
        } catch (e) {
          console.warn('Difference failed:', e);
        }
      });

      this.updateGridFromPolygon(playerId, territory.polygon);

      // 꼬리 단절 체크 (영역 확장 중인 플레이어는 제외)
      this.checkTrailCuts(playerId);

      this.render();
      return trailPoints;
    } catch (e) {
      console.warn('Expand failed:', e);
      return null;
    }
  }

  /**
   * 트레일을 닫힌 폴리곤으로 변환
   *
   * Paper.io 핵심 로직:
   * 1. 트레일 시작점(EXIT point) - 영역을 나간 지점
   * 2. 트레일 끝점(ENTRY point) - 영역으로 돌아온 지점
   * 3. 두 점 사이의 더 짧은 경계 경로를 선택
   * 4. 트레일 + 경계 경로로 닫힌 폴리곤 생성
   */
  private createTrailPolygon(
    existingPoly: MultiPolygon,
    trailPoints: { x: number; y: number }[]
  ): Polygon | null {
    if (trailPoints.length < 2) return null;

    // 기존 영역이 없으면 트레일만으로 폴리곤 생성
    if (existingPoly.length === 0 || existingPoly[0].length === 0) {
      const ring: Ring = trailPoints.map(p => [p.x, p.y]);
      ring.push(ring[0]);
      return [ring];
    }

    const outerRing = existingPoly[0][0];
    if (!outerRing || outerRing.length < 3) {
      const ring: Ring = trailPoints.map(p => [p.x, p.y]);
      ring.push(ring[0]);
      return [ring];
    }

    // EXIT point (트레일 시작) 와 ENTRY point (트레일 끝)
    const exitPoint = trailPoints[0];
    const entryPoint = trailPoints[trailPoints.length - 1];

    // 경계선에서 가장 가까운 점의 인덱스와 실제 교차점 찾기
    const exitIdx = this.findClosestPointIndex(outerRing, exitPoint);
    const entryIdx = this.findClosestPointIndex(outerRing, entryPoint);

    // 두 방향으로 경계를 따라가며 어느 쪽이 더 짧은지 계산
    const n = outerRing.length - 1; // 마지막 점은 첫 점과 동일 (닫힌 폴리곤)

    // 시계 방향 (forward) 경로
    const forwardPath = this.getBoundaryPath(outerRing, entryIdx, exitIdx, 1, n);
    // 반시계 방향 (backward) 경로
    const backwardPath = this.getBoundaryPath(outerRing, entryIdx, exitIdx, -1, n);

    // 더 짧은 경로 선택
    const forwardLen = this.calculatePathLength(forwardPath);
    const backwardLen = this.calculatePathLength(backwardPath);
    const boundaryPath = forwardLen <= backwardLen ? forwardPath : backwardPath;

    // 트레일 + 경계 경로로 닫힌 폴리곤 생성
    const trailRing: Ring = trailPoints.map(p => [p.x, p.y]);
    const fullRing: Ring = [...trailRing, ...boundaryPath];

    // 폴리곤 닫기
    const first = fullRing[0];
    const last = fullRing[fullRing.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      fullRing.push([first[0], first[1]]);
    }

    return [fullRing];
  }

  /**
   * 경계선을 따라 시작점에서 끝점까지의 경로 추출
   */
  private getBoundaryPath(
    ring: Ring,
    startIdx: number,
    endIdx: number,
    direction: 1 | -1,
    n: number
  ): Coord[] {
    const path: Coord[] = [];
    let idx = startIdx;
    let count = 0;
    const maxIterations = n + 1;

    while (count < maxIterations) {
      path.push(ring[idx]);
      if (idx === endIdx) break;
      idx = (idx + direction + n) % n;
      count++;
    }

    return path;
  }

  /**
   * 경로의 총 길이 계산
   */
  private calculatePathLength(path: Coord[]): number {
    if (path.length < 2) return 0;
    let len = 0;
    for (let i = 1; i < path.length; i++) {
      const dx = path[i][0] - path[i - 1][0];
      const dy = path[i][1] - path[i - 1][1];
      len += Math.sqrt(dx * dx + dy * dy);
    }
    return len;
  }

  /**
   * 포인트 배열 정리 (중복 제거, 너무 가까운 점 병합)
   */
  private cleanupPoints(points: { x: number; y: number }[]): { x: number; y: number }[] {
    if (points.length < 2) return points;

    const minDistance = 2; // 최소 거리
    const result: { x: number; y: number }[] = [points[0]];

    for (let i = 1; i < points.length; i++) {
      const prev = result[result.length - 1];
      const curr = points[i];
      const dist = Math.sqrt((curr.x - prev.x) ** 2 + (curr.y - prev.y) ** 2);

      if (dist >= minDistance) {
        result.push(curr);
      }
    }

    return result;
  }

  /**
   * 폴리곤 유효성 검사
   */
  private isValidPolygon(polygon: Polygon): boolean {
    if (!polygon || polygon.length === 0) return false;

    const ring = polygon[0];
    if (!ring || ring.length < 4) return false; // 최소 3개 점 + 닫기

    // 최소 면적 검사
    const area = this.calculateArea(ring);
    if (area < 100) return false; // 너무 작은 영역 무시

    return true;
  }

  /**
   * 유효한 폴리곤만 필터링
   */
  private filterValidPolygons(multiPoly: MultiPolygon): MultiPolygon {
    return multiPoly.filter(polygon => {
      if (!polygon || polygon.length === 0) return false;
      const ring = polygon[0];
      if (!ring || ring.length < 4) return false;
      const area = this.calculateArea(ring);
      return area >= 100;
    });
  }

  private findClosestPointIndex(ring: Ring, point: { x: number; y: number }): number {
    let minDist = Infinity;
    let minIdx = 0;
    for (let i = 0; i < ring.length; i++) {
      const dist = (ring[i][0] - point.x) ** 2 + (ring[i][1] - point.y) ** 2;
      if (dist < minDist) {
        minDist = dist;
        minIdx = i;
      }
    }
    return minIdx;
  }

  /**
   * 분할된 폴리곤 중 캐릭터가 있는/가까운 폴리곤 선택
   *
   * Paper.io 핵심 로직:
   * 1. 캐릭터가 폴리곤 안에 있으면 그 폴리곤 유지
   * 2. 캐릭터가 트레일 위에 있으면 (본진 밖) 폴리곤 중심과 가장 가까운 폴리곤 유지
   */
  private keepPolygonWithPoint(multiPoly: MultiPolygon, point: { x: number; y: number }): MultiPolygon {
    if (multiPoly.length === 0) return multiPoly;
    if (multiPoly.length === 1) return multiPoly;

    // 1단계: 캐릭터가 폴리곤 안에 있는지 확인
    for (const polygon of multiPoly) {
      if (polygon.length > 0 && polygon[0].length > 2) {
        if (this.pointInPolygon(point.x, point.y, polygon[0])) {
          return [polygon];
        }
      }
    }

    // 2단계: 캐릭터가 어느 폴리곤에도 없음 (트레일 위에 있음)
    // 폴리곤 중심점과 가장 가까운 폴리곤을 찾아서 반환
    let closestPolygon = multiPoly[0];
    let minDistance = Infinity;

    for (const polygon of multiPoly) {
      const ring = polygon[0];
      if (!ring || ring.length < 3) continue;

      // 폴리곤 중심점 계산
      const center = this.getPolygonCenter(ring);
      const dist = Math.sqrt((point.x - center.x) ** 2 + (point.y - center.y) ** 2);

      if (dist < minDistance) {
        minDistance = dist;
        closestPolygon = polygon;
      }
    }

    return [closestPolygon];
  }

  /**
   * 폴리곤의 중심점 계산
   */
  private getPolygonCenter(ring: Ring): { x: number; y: number } {
    let cx = 0, cy = 0;
    const len = ring.length - 1; // 마지막 점은 첫 점과 동일
    for (let i = 0; i < len; i++) {
      cx += ring[i][0];
      cy += ring[i][1];
    }
    return { x: cx / len, y: cy / len };
  }

  private calculateArea(ring: Ring): number {
    if (ring.length < 3) return 0;
    let area = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      area += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
    }
    return Math.abs(area / 2);
  }

  private resetTerritoryAroundPoint(playerId: string, center: { x: number; y: number }, _color: number): void {
    const radius = 60;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 24; i++) {
      const angle = (i / 24) * Math.PI * 2;
      points.push({
        x: center.x + Math.cos(angle) * radius,
        y: center.y + Math.sin(angle) * radius,
      });
    }
    const newPoly: MultiPolygon = [this.pointsToClipper(points)];
    const territory = this.territories.get(playerId);
    if (territory) {
      territory.polygon = newPoly;
      this.updateGridFromPolygon(playerId, newPoly);
    }
  }

  resetTerritory(playerId: string, x: number, y: number, color: number, radius: number = 80): void {
    // 기존 그리드 데이터 제거
    for (let gy = 0; gy < this.gridHeight; gy++) {
      for (let gx = 0; gx < this.gridWidth; gx++) {
        if (this.grid[gy][gx] === playerId) {
          this.grid[gy][gx] = null;
        }
      }
    }
    this.createInitialTerritory(playerId, x, y, color, radius);
  }

  getTerritory(playerId: string): { points: { x: number; y: number }[] } | undefined {
    const territory = this.territories.get(playerId);
    if (!territory || territory.polygon.length === 0) return undefined;
    const points = this.clipperToPoints(territory.polygon);
    return points.length > 0 ? { points: points[0] } : undefined;
  }

  /**
   * 영역 전체 스냅샷 (늦게 입장한 클라이언트 동기화용)
   * 좌표는 전송 페이로드 절약을 위해 정수로 반올림한다.
   */
  getTerritorySnapshot(playerId: string): { color: number; polygon: [number, number][][][] } | undefined {
    const territory = this.territories.get(playerId);
    if (!territory || territory.polygon.length === 0) return undefined;

    const polygon = territory.polygon.map((poly) =>
      poly.map((ring) =>
        ring.map(([px, py]) => [Math.round(px), Math.round(py)] as [number, number])
      )
    );

    return { color: territory.color, polygon };
  }

  /**
   * 스냅샷으로 영역 전체를 교체 (소유자가 보낸 상태가 항상 권위를 가진다)
   */
  setTerritorySnapshot(playerId: string, color: number, polygon: [number, number][][][]): void {
    if (!Array.isArray(polygon) || polygon.length === 0) return;

    const simplified = this.simplifyMultiPolygon(polygon);
    if (simplified.length === 0) return;

    this.territories.set(playerId, { playerId, color, polygon: simplified });
    this.updateGridFromPolygon(playerId, simplified);
    this.render();
  }

  getMinimapData(): { playerId: string; color: number; points: { x: number; y: number }[] }[] {
    const result: { playerId: string; color: number; points: { x: number; y: number }[] }[] = [];
    this.territories.forEach((territory) => {
      const points = this.clipperToPoints(territory.polygon);
      if (points.length > 0 && points[0].length > 0) {
        result.push({
          playerId: territory.playerId,
          color: territory.color,
          points: points[0],
        });
      }
    });
    return result;
  }

  getTerritoryArea(playerId: string): number {
    const cached = this.areaCache.get(playerId);
    if (cached !== undefined) return cached;

    const territory = this.territories.get(playerId);
    if (!territory) return 0;
    let totalArea = 0;
    for (const polygon of territory.polygon) {
      if (polygon[0]) {
        totalArea += this.calculateArea(polygon[0]);
      }
    }
    this.areaCache.set(playerId, totalArea);
    return totalArea;
  }

  /**
   * 전체 맵 면적 반환
   */
  getTotalWorldArea(): number {
    return Math.PI * this.worldRadius * this.worldRadius;
  }

  /**
   * 플레이어 영역이 전체 맵에서 차지하는 비율 (%)
   */
  getTerritoryPercent(playerId: string): number {
    const playerArea = this.getTerritoryArea(playerId);
    const worldArea = this.getTotalWorldArea();
    if (worldArea === 0) return 0;
    return (playerArea / worldArea) * 100;
  }

  private static readonly MIN_TERRITORY_AREA = 25000; // 초기 영역(~20000)보다 커야 부스터 제한
  private gridUpdateTimer: Map<string, number> = new Map();

  /**
   * 부스트 가능 여부 확인
   */
  canBoost(playerId: string): boolean {
    const currentArea = this.getTerritoryArea(playerId);
    return currentArea > TerritoryManager.MIN_TERRITORY_AREA;
  }

  /**
   * 부스트 시 영역 소모 - 폴리곤을 중심 방향으로 축소
   */
  consumeTerritory(playerId: string, delta: number): boolean {
    const territory = this.territories.get(playerId);
    if (!territory || territory.polygon.length === 0) return false;

    // 최소 영역 크기 체크
    const currentArea = this.getTerritoryArea(playerId);
    if (currentArea <= TerritoryManager.MIN_TERRITORY_AREA) return false;

    // 축소 비율 (초당 2% 축소)
    const shrinkRate = 0.02 * (delta / 1000);
    const scaleFactor = 1 - shrinkRate;

    // 꼬리 뿌리(trailRoot)도 영역과 함께 같은 비율로 당겨야 한다.
    // 그러지 않으면 부스터로 영역이 안쪽으로 줄 때 고정된 trailRoot가
    // 영역 밖으로 노출되고, 다른 플레이어의 expandTerritory가 트리거하는
    // checkTrailCuts가 이를 "꼬리 절단"으로 오판해 부당하게 죽는다.
    // (남이 영역을 빼앗는 경우는 이 함수가 아니라 difference 경로라 영향 없음)
    const root = this.trailRoots.get(playerId);
    let rootHostCx = 0, rootHostCy = 0, rootBestDistSq = Infinity;

    // 각 폴리곤을 중심 방향으로 축소
    const newPolygon: MultiPolygon = [];
    for (const polygon of territory.polygon) {
      const ring = polygon[0];
      if (!ring || ring.length < 3) continue;

      // 폴리곤 중심 계산
      let cx = 0, cy = 0;
      for (const point of ring) {
        cx += point[0];
        cy += point[1];
      }
      cx /= ring.length;
      cy /= ring.length;

      // trailRoot가 속할 가능성이 가장 높은(가장 가까운) 폴리곤 중심을 기록
      if (root) {
        const dsq = (root.x - cx) ** 2 + (root.y - cy) ** 2;
        if (dsq < rootBestDistSq) {
          rootBestDistSq = dsq;
          rootHostCx = cx;
          rootHostCy = cy;
        }
      }

      // 중심 방향으로 축소
      const newRing: Ring = ring.map(point => {
        const nx = cx + (point[0] - cx) * scaleFactor;
        const ny = cy + (point[1] - cy) * scaleFactor;
        return [nx, ny] as Coord;
      });

      newPolygon.push([newRing]);
    }

    territory.polygon = newPolygon;

    // trailRoot를 같은 중심·비율로 변환해 영역 경계와의 상대 위치 유지
    if (root && rootBestDistSq !== Infinity) {
      this.trailRoots.set(playerId, {
        x: rootHostCx + (root.x - rootHostCx) * scaleFactor,
        y: rootHostCy + (root.y - rootHostCy) * scaleFactor,
      });
    }
    // 그리드 업데이트가 쓰로틀링되어도 면적 캐시는 즉시 무효화
    this.areaCache.delete(playerId);

    // 그리드 업데이트 쓰로틀링 (200ms마다)
    const lastUpdate = this.gridUpdateTimer.get(playerId) || 0;
    const now = Date.now();
    if (now - lastUpdate > 200) {
      this.updateGridFromPolygon(playerId, newPolygon);
      this.gridUpdateTimer.set(playerId, now);
    }

    return true;
  }

  private toBrightColor(color: number): number {
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;
    const brightR = Math.min(255, Math.floor(r + (255 - r) * 0.15));
    const brightG = Math.min(255, Math.floor(g + (255 - g) * 0.15));
    const brightB = Math.min(255, Math.floor(b + (255 - b) * 0.15));
    return (brightR << 16) | (brightG << 8) | brightB;
  }

  private darkenColor(color: number, amount: number): number {
    const r = Math.max(0, ((color >> 16) & 0xFF) * (1 - amount));
    const g = Math.max(0, ((color >> 8) & 0xFF) * (1 - amount));
    const b = Math.max(0, (color & 0xFF) * (1 - amount));
    return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
  }

  /**
   * 저장용 MultiPolygon 단순화.
   * union/difference를 반복하면 trail 점(10px 간격)이 그대로 누적되어
   * 정점 수가 무한정 늘고, 이후의 모든 연산(클리핑·그리드·렌더·면적·전송)이
   * 함께 느려진다. 폴리곤을 변경할 때마다 호출해 정점 밀도를 상한선 아래로 유지한다.
   */
  private simplifyMultiPolygon(multiPoly: MultiPolygon, tolerance: number = 3): MultiPolygon {
    const result: MultiPolygon = [];
    for (const polygon of multiPoly) {
      const simplified: Polygon = [];
      for (const ring of polygon) {
        const s = this.simplifyRing(ring, tolerance);
        if (s.length >= 4) simplified.push(s);
      }
      if (simplified.length > 0 && simplified[0].length >= 4) {
        result.push(simplified);
      }
    }
    return result;
  }

  /**
   * 폴리곤 단순화 (Douglas-Peucker 알고리즘 간소화 버전)
   */
  private simplifyRing(ring: Ring, tolerance: number = 3): Ring {
    if (ring.length <= 10) return ring;

    const result: Ring = [ring[0]];
    let lastPoint = ring[0];

    for (let i = 1; i < ring.length - 1; i++) {
      const dx = ring[i][0] - lastPoint[0];
      const dy = ring[i][1] - lastPoint[1];
      if (dx * dx + dy * dy > tolerance * tolerance) {
        result.push(ring[i]);
        lastPoint = ring[i];
      }
    }

    result.push(ring[ring.length - 1]);
    return result;
  }

  render(): void {
    this.graphics.clear();

    this.territories.forEach((territory) => {
      const brightColor = this.toBrightColor(territory.color);
      const shadowColor = this.darkenColor(territory.color, 0.3);

      for (const polygon of territory.polygon) {
        let ring = polygon[0];
        if (!ring || ring.length < 3) continue;

        ring = this.simplifyRing(ring, 4);

        // 그림자
        this.graphics.fillStyle(shadowColor, 1);
        this.graphics.beginPath();
        this.graphics.moveTo(ring[0][0], ring[0][1] + 10);
        for (let i = 1; i < ring.length; i++) {
          this.graphics.lineTo(ring[i][0], ring[i][1] + 10);
        }
        this.graphics.closePath();
        this.graphics.fillPath();

        // 메인 영역
        this.graphics.fillStyle(brightColor, 1);
        this.graphics.beginPath();
        this.graphics.moveTo(ring[0][0], ring[0][1]);
        for (let i = 1; i < ring.length; i++) {
          this.graphics.lineTo(ring[i][0], ring[i][1]);
        }
        this.graphics.closePath();
        this.graphics.fillPath();

        // 외곽선
        this.graphics.lineStyle(2, this.darkenColor(territory.color, 0.2), 1);
        this.graphics.strokePath();
      }
    });
  }

  getAllTerritories(): Map<string, Territory> {
    return this.territories;
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
